// src/components/profile/ExecutiveOverview/ExecutiveOverview.jsx
import React from 'react';
import HeroSummary from './HeroSummary';
import TrioCards from './TrioCards';
import EvidenceBand from './EvidenceBand';
import NextActions from './NextActions';
import NarrativeDisclosure from './NarrativeDisclosure';
import { Provenance } from '../../../utils/provenance';

// Status pill component
const StatusPill = ({ status }) => {
  const statusConfig = {
    'advance': { bg: 'bg-green-100', text: 'text-green-800', label: 'Advance' },
    'evaluate': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Evaluate' },
    'park': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Park' },
    'proceed': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Proceed' }
  };
  
  const config = statusConfig[status?.toLowerCase()] || statusConfig['evaluate'];
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

export default function ExecutiveOverview({ data }) {
  // Comprehensive null checks with fallback data
  if (!data) {
    return (
      <div className="p-4 text-center">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
        <p className="mt-3 text-gray-500 text-sm">Loading overview data...</p>
      </div>
    );
  }
  
  // Provide default structure if data is partially missing
  const safeData = {
    hero_takeaway: data.hero_takeaway || 'Delivers measurable ROI through strategic data product implementation.',
    market_tags: data.market_tags || [],
    problem: data.problem || {
      bullets: [
        'Complex market dynamics requiring data-driven insights',
        'Limited visibility into operational efficiency',
        'Strategic decisions hindered by fragmented information'
      ],
      source: 'use_case_profiles.market_problem',
      updatedAt: null
    },
    solution: data.solution || {
      bullets: [
        'Integrated data analytics platform for insights',
        'Automated processing of multiple data sources',
        'Real-time dashboards and predictive modeling'
      ],
      source: 'use_case_profiles.product_solution',
      updatedAt: null
    },
    value: data.value || {
      bullets: [
        'Enhanced operational efficiency and cost optimization',
        'Improved strategic decision-making with data insights',
        'Reduced risk through predictive analytics'
      ],
      source: 'use_case_profiles.business_value',
      updatedAt: null
    },
    recommendation: data.recommendation || null,
    narrative: data.narrative || null,
    evidence: data.evidence || null,
    dataProvenance: data.dataProvenance || []
  };
  
  return (
    <div className="space-y-3"> {/* Reduced from space-y-6 */}
      {/* Hero Summary - compact version */}
      {safeData.hero_takeaway && (
        <HeroSummary 
          takeaway={safeData.hero_takeaway} 
          tags={safeData.market_tags}
        />
      )}
      
      {/* Trio Cards with tighter spacing */}
      <TrioCards
        problem={safeData.problem}
        solution={safeData.solution}
        value={safeData.value}
      />
      
      {/* Executive Recommendation section - more compact */}
      {safeData.recommendation && (
        <section className="rounded-lg border bg-white shadow-sm p-4"> {/* Reduced from p-6 and rounded-2xl */}
          <div className="flex items-center justify-between mb-3"> {/* Reduced from mb-4 */}
            <h2 className="text-base font-semibold text-slate-900"> {/* Reduced from text-lg */}
              Executive Recommendation
            </h2>
            <Provenance 
              source={safeData.recommendation.source || "use_case_profiles.recommendation"} 
              updatedAt={safeData.recommendation.updatedAt}
            />
          </div>
          
          <div className="space-y-3"> {/* Reduced from space-y-4 */}
            {/* Status and one-line why */}
            <div className="flex items-start gap-2"> {/* Reduced from gap-3 */}
              <StatusPill status={safeData.recommendation.status} />
              <p className="text-xs text-slate-600"> {/* Reduced from text-sm */}
                {safeData.recommendation.statusReason || 'Strong ROI potential with manageable implementation complexity'}
              </p>
            </div>
            
            {/* Rationale bullets */}
            {safeData.recommendation.rationale && safeData.recommendation.rationale.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-slate-700 mb-1">Rationale</h4> {/* Reduced from text-sm and mb-2 */}
                <ul className="space-y-0.5"> {/* Reduced from space-y-1 */}
                  {safeData.recommendation.rationale.slice(0, 3).map((item, i) => (
                    <li key={i} className="text-xs text-slate-600 flex items-start"> {/* Reduced from text-sm */}
                      <span className="mr-1.5">•</span> {/* Reduced from mr-2 */}
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Next Actions - will inherit compact sizing */}
            <NextActions actions={safeData.recommendation.nextSteps} />
          </div>
        </section>
      )}
      
      {/* Detailed Analysis - collapsed by default */}
      {safeData.narrative && (
        <NarrativeDisclosure title="Detailed Analysis">
          <div 
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: safeData.narrative }} 
          />
        </NarrativeDisclosure>
      )}
      
      {/* Evidence Band */}
      {(safeData.evidence || safeData.dataProvenance.length > 0) && (
        <EvidenceBand 
          items={safeData.evidence} 
          evidence={safeData.dataProvenance}
        />
      )}
    </div>
  );
}